import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Dashboard from "@/components/Dashboard";
import StagePage from "@/components/StagePage";
import StoresPage from "@/components/StoresPage";
import ScrapSalesPage from "@/components/ScrapSalesPage";
import AnalystPage from "@/components/analyst/AnalystPage";
import BlockedBoardPage from "@/components/BlockedBoardPage";
import StockLayout from "@/components/StockLayout";
import StockCenterPage from "@/components/StockCenterPage";
import EcotileLayout from "@/components/EcotileLayout";
import MaintenanceLayout from "@/components/MaintenanceLayout";
import MaintenanceDashboard from "@/components/MaintenanceDashboard";
import MaintenanceStagePage from "@/components/MaintenanceStagePage";
import MaintenancePmPage from "@/components/MaintenancePmPage";
import MaintenanceSparesPage from "@/components/MaintenanceSparesPage";
import MaintenanceRequestsPage from "@/components/MaintenanceRequestsPage";
import EcotilePage from "@/components/EcotilePage";
import Changelog from "@/components/Changelog";
import LoginPage from "@/components/LoginPage";
import GlobalFilterPanel from "@/components/GlobalFilterPanel";
import ErrorBoundary from "@/components/ErrorBoundary";
import { DueFilterProvider } from "@/contexts/DueFilterContext";
import { lockedDivisionFor, isValidLens, DIVISION_CONFIG_FALLBACK } from "@/lib/divisions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// No request may hang forever: a stuck call would otherwise jam the browser's
// 6-connections-per-host pool and freeze every poll behind it (the "Offline
// until manual refresh" symptom). 30s is far above any legitimate request.
axios.defaults.timeout = 30000;

const STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];

// ── Contexts ──
export const AuthContext = createContext();
export const RoleContext = createContext();
export const ThemeContext = createContext();

// Split data contexts
export const DataStateContext   = createContext(); // { items, dashboardStats, loading, isRefreshing }
export const DataActionsContext = createContext(); // { patchItem, refreshData, ...mutations }

// Backward-compat bridge — existing consumers keep working unchanged
export const DataContext = createContext();

export const FilterContext = createContext();
export const DivisionLensContext = createContext(); // { lens, setLens, lockedDivision }
export const DivisionConfigContext = createContext(); // { config, refreshDivisionConfig }
export const AdvanceContext = createContext();
export const SettingsContext = createContext();
export const ClientsContext = createContext();
export const ClientGroupsContext = createContext();

// ── Capability helpers (read straight off the user — no contexts needed) ──
function hasStockCenterAccess(user) {
  if (!user) return false;
  if (user.role === "Admin") return true;
  return !!user.permissions?.can_view_stock_center;
}
// Warehouse-only users carry the dedicated "Stock Center" role and have no ERP
// footing. Everyone else with the permission is dual-access (lands in the ERP
// and switches into the Stock Center).
function isWarehouseOnly(user) {
  return !!user && user.role === "Stock Center";
}

function hasEcotileAccess(user) {
  if (!user) return false;
  if (user.role === "Admin") return true;
  return !!user.permissions?.can_view_ecotile;
}
// Ecotile-only users carry the dedicated "Ecotile" role and have no ERP footing.
function isEcotileOnly(user) {
  return !!user && user.role === "Ecotile";
}

function hasMaintenanceAccess(user) {
  if (!user) return false;
  if (user.role === "Admin") return true;
  return !!user.permissions?.can_view_maintenance;
}
// Maintenance-only users carry the dedicated "Maintenance" role and land in
// the Maintenance shell straight from login. Everyone else with the permission
// (production, admins) is dual-access and reaches it from Modules.
function isMaintenanceOnly(user) {
  return !!user && user.role === "Maintenance";
}

// Minimal theme application for the warehouse-only shell, which never mounts
// MainApp (where the ERP's theme machinery lives). Mirrors the resolved-theme
// logic: light/dark/system, persisted under the same rl_theme key.
function useStandaloneTheme() {
  React.useEffect(() => {
    const apply = () => {
      let pref = "system";
      try { const s = localStorage.getItem("rl_theme"); if (["light","dark","system"].includes(s)) pref = s; } catch {}
      let dark = false;
      if (pref === "dark") dark = true;
      else if (pref === "system") { try { dark = window.matchMedia("(prefers-color-scheme: dark)").matches; } catch {} }
      document.documentElement.className = dark ? "dark" : "light";
      try { if (localStorage.getItem("rl_flat") === "1") document.documentElement.setAttribute("data-flat", "1"); } catch {}
      try { if (localStorage.getItem("rl_easyread") === "1") document.documentElement.setAttribute("data-easyread", "1"); } catch {}
    };
    apply();
    let mq;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); mq.addEventListener?.("change", apply); } catch {}
    return () => { try { mq?.removeEventListener?.("change", apply); } catch {} };
  }, []);
}

// The four Maintenance stage routes — declared once and mounted in both the
// standalone shell and the dual-access tree so the two can never drift.
const MAINTENANCE_STAGE_ROUTES = (
  <>
    <Route path="/maintenance/received"     element={<ErrorBoundary><MaintenanceStagePage stage="received" /></ErrorBoundary>} />
    <Route path="/maintenance/under-repair" element={<ErrorBoundary><MaintenanceStagePage stage="repair" /></ErrorBoundary>} />
    <Route path="/maintenance/handover"     element={<ErrorBoundary><MaintenanceStagePage stage="handover" /></ErrorBoundary>} />
    <Route path="/maintenance/complete"     element={<ErrorBoundary><MaintenanceStagePage stage="complete" /></ErrorBoundary>} />
  </>
);

// The three Phase-2 surfaces that hang off the nav AFTER the pipeline groove —
// same both-shells rule as the stage routes above.
const MAINTENANCE_EXTRA_ROUTES = (
  <>
    <Route path="/maintenance/pm"       element={<ErrorBoundary><MaintenancePmPage /></ErrorBoundary>} />
    <Route path="/maintenance/spares"   element={<ErrorBoundary><MaintenanceSparesPage /></ErrorBoundary>} />
    <Route path="/maintenance/requests" element={<ErrorBoundary><MaintenanceRequestsPage /></ErrorBoundary>} />
  </>
);

// Standalone Stock Center app for warehouse-only users. Deliberately does NOT
// load the ERP data contexts — it is a separate world.
function StockCenterApp() {
  useStandaloneTheme();
  return (
    <Routes>
      <Route element={<StockLayout />}>
        <Route path="/stock-center" element={<ErrorBoundary><StockCenterPage /></ErrorBoundary>} />
        {/* Any other path for a warehouse-only user routes home to the shell. */}
        <Route path="*" element={<Navigate to="/stock-center" replace />} />
      </Route>
    </Routes>
  );
}

// Standalone Ecotile portal for Ecotile-only users. Like StockCenterApp, it
// does NOT load the ERP data contexts.
function EcotileApp() {
  useStandaloneTheme();
  return (
    <Routes>
      <Route element={<EcotileLayout />}>
        <Route path="/ecotile" element={<ErrorBoundary><EcotilePage /></ErrorBoundary>} />
        <Route path="*" element={<Navigate to="/ecotile" replace />} />
      </Route>
    </Routes>
  );
}

// Standalone Maintenance shell for maintenance-only users. Like StockCenterApp
// it does NOT load the ERP data contexts — it is a separate world.
function MaintenanceApp() {
  useStandaloneTheme();
  return (
    <Routes>
      <Route element={<MaintenanceLayout />}>
        <Route path="/maintenance" element={<ErrorBoundary><MaintenanceDashboard /></ErrorBoundary>} />
        {MAINTENANCE_STAGE_ROUTES}
        {MAINTENANCE_EXTRA_ROUTES}
        <Route path="*" element={<Navigate to="/maintenance" replace />} />
      </Route>
    </Routes>
  );
}

// ── Protected wrapper ──
function ProtectedContent() {
  const { user } = useContext(AuthContext);
  if (!user) return <Navigate to="/login" replace />;
  // Warehouse-only users get ONLY the Stock Center shell — the ERP never mounts.
  if (isWarehouseOnly(user)) return <StockCenterApp />;
  if (isEcotileOnly(user)) return <EcotileApp />;
  if (isMaintenanceOnly(user)) return <MaintenanceApp />;
  return <MainApp />;
}

// ── Main application (mounted only when authenticated) ──
function MainApp() {
  const { user } = useContext(AuthContext);
  // Theme preference: "light" | "dark" | "system" ("system" follows the OS
  // appearance live). rl_theme previously stored only light/dark, so old
  // saved values remain valid. No saved value (new device / cleared storage)
  // → default to "system" (Auto) per product decision 11-Jun-26.
  const [themePref, setThemePrefState] = useState(() => {
    try {
      const saved = localStorage.getItem("rl_theme");
      return ["light", "dark", "system"].includes(saved) ? saved : "system";
    } catch { return "system"; }
  });
  const [systemDark, setSystemDark] = useState(() => {
    try { return window.matchMedia("(prefers-color-scheme: dark)").matches; }
    catch { return false; }
  });
  // Resolved theme — what the UI actually wears. All consumers keep reading
  // `theme` as before; only the pref/raw value knows about "system".
  const theme = themePref === "system" ? (systemDark ? "dark" : "light") : themePref;
  // Performance Mode (flat UI): independent of light/dark. Persisted separately.
  const [flatMode, setFlatMode] = useState(() => {
    try { return localStorage.getItem("rl_flat") === "1"; }
    catch { return false; }
  });
  // Easy-read text: swaps the three --rl-font-* tokens for a rounder, more open
  // set (Sora / Clash Display / Bricolage Grotesque). Per-browser, like flat mode.
  const [easyRead, setEasyRead] = useState(() => {
    try { return localStorage.getItem("rl_easyread") === "1"; }
    catch { return false; }
  });
  const [items, setItems] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(() => Date.now());
  const [globalFilter, setGlobalFilter] = useState({ clients: null, priority: "all", flag: null });
  const [divisionLens, setDivisionLens] = useState("all");

  // Restore this user's saved filter on login
  useEffect(() => {
    if (!user?.id) return;
    try {
      const saved = localStorage.getItem(`rl_filter_${user.id}`);
      setGlobalFilter(saved ? JSON.parse(saved) : { clients: null, priority: "all", flag: null });
    } catch (e) { /* ignore */ }
  }, [user?.id]);

  // Division lens (multi-division Phase 1b). Persisted per user; users whose
  // division_access allows exactly one division get the lens locked to it.
  // NOTE the user-id shape split: the login response carries `id`, but
  // /auth/me (every reload) carries `user_id` — accept both or persistence
  // silently breaks after reloads.
  const lensUserId = user?.id || user?.user_id;
  const lensLockedDivision = lockedDivisionFor(user);
  useEffect(() => {
    if (!lensUserId) return;
    if (lensLockedDivision) { setDivisionLens(lensLockedDivision); return; }
    try {
      const saved = localStorage.getItem(`rl_division_lens_${lensUserId}`);
      setDivisionLens(saved && isValidLens(saved) ? saved : "all");
    } catch (e) { /* ignore */ }
  }, [lensUserId, lensLockedDivision]);
  const setDivisionLensPersist = useCallback((v) => {
    if (lensLockedDivision || !isValidLens(v)) return;
    setDivisionLens(v);
    try { if (lensUserId) localStorage.setItem(`rl_division_lens_${lensUserId}`, v); } catch (e) { /* ignore */ }
  }, [lensUserId, lensLockedDivision]);

  // Per-division pipeline config (Phase 1b): statuses/sub-teams/gates per
  // division, admin-editable. Fallback keeps the UI safe pre-fetch/offline.
  const [divisionConfig, setDivisionConfig] = useState(DIVISION_CONFIG_FALLBACK);
  const refreshDivisionConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/division-config`);
      if (res.data && res.data.metallic) setDivisionConfig(res.data);
    } catch (e) { /* keep fallback */ }
  }, []);
  useEffect(() => { if (user) refreshDivisionConfig(); }, [user, refreshDivisionConfig]);

  // Persist on change
  useEffect(() => {
    if (!user?.id) return;
    try { localStorage.setItem(`rl_filter_${user.id}`, JSON.stringify(globalFilter)); } catch (e) { /* ignore */ }
  }, [globalFilter, user?.id]);
  const [lastAdvanceInfo, setLastAdvanceInfo] = useState(null);
  const [settings, setSettings] = useState({ inr_rate: 95, due_this_week_days: 7, company_name: "Rockpecker Pvt Ltd", company_location: "Bhateri, Punjab, India" });
  const [clients, setClients] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const itemsRef = useRef([]);
  const statsDebounceRef = useRef(null);
  const lastRefreshTimeRef = useRef(Date.now());

  useEffect(() => { itemsRef.current = items; }, [items]);

  useEffect(() => {
    // Set BOTH classes so:
    //   • the existing `.light` CSS-variable overrides keep working
    //   • Tailwind's `dark:` variants fire correctly when in dark mode
    document.documentElement.className = theme === "light" ? "light" : "dark";
  }, [theme]);

  // Performance Mode: set a data-flat attribute on <html>, independent of the
  // theme class above (the class is overwritten on every theme switch, so flat
  // mode rides on an attribute instead). CSS keys off html[data-flat="1"].
  useEffect(() => {
    if (flatMode) document.documentElement.setAttribute("data-flat", "1");
    else document.documentElement.removeAttribute("data-flat");
  }, [flatMode]);
  useEffect(() => {
    if (easyRead) {
      document.documentElement.setAttribute("data-easyread", "1");
      // Fetch the two Google families only once the mode is first switched on.
      if (!document.getElementById("rl-easyread-fonts")) {
        const link = document.createElement("link");
        link.id = "rl-easyread-fonts"; link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&display=swap";
        document.head.appendChild(link);
        // Clash Display is self-hosted (public/fonts). Declared here rather than
        // in index.css because CRA's css-loader would try to bundle a /fonts URL.
        const face = document.createElement("style");
        face.id = "rl-easyread-clash";
        face.textContent = `@font-face{font-family:'Clash Display';src:url('${process.env.PUBLIC_URL || ""}/fonts/ClashDisplay-Variable.woff2') format('woff2');font-weight:200 700;font-display:swap}`;
        document.head.appendChild(face);
      }
    } else document.documentElement.removeAttribute("data-easyread");
  }, [easyRead]);

  // Cleanup debounce timer on unmount
  useEffect(() => () => clearTimeout(statsDebounceRef.current), []);

  // Follow the OS appearance while the pref is "system".
  useEffect(() => {
    if (themePref !== "system") return;
    let mq;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return; }
    const onChange = e => setSystemDark(e.matches);
    setSystemDark(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [themePref]);

  const setThemePref = useCallback((pref) => {
    setThemePrefState(pref);
    try { localStorage.setItem("rl_theme", pref); } catch {}
  }, []);

  // Keyboard-shortcut cycle: Light → Auto → Dark → Light. Returns the pref it
  // just switched to so the caller can flash a label. Reads the current pref
  // from a ref so the callback stays stable for Layout's empty-deps listener.
  const themePrefRef = useRef(themePref);
  useEffect(() => { themePrefRef.current = themePref; }, [themePref]);
  const cycleTheme = useCallback(() => {
    const order = ["light", "system", "dark"];
    const next = order[(order.indexOf(themePrefRef.current) + 1) % order.length];
    setThemePref(next);
    return next;
  }, [setThemePref]);

  const toggleFlat = useCallback(() => {
    setFlatMode(prev => {
      const next = !prev;
      try { localStorage.setItem("rl_flat", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  const toggleEasyRead = useCallback(() => {
    setEasyRead(prev => {
      const next = !prev;
      try { localStorage.setItem("rl_easyread", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  const canEditStage = useCallback((stage) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    return user.permissions?.stages?.[stage] === "edit";
  }, [user]);

  const canViewStage = useCallback((stage) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    const access = user.permissions?.stages?.[stage];
    return access === "edit" || access === "view";
  }, [user]);

  const canDo = useCallback((permission) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    const val = user.permissions?.[permission];
    // can_edit_items defaults to true for backward compat (migration backfills DB, but frontend needs safe fallback)
    if (val === undefined && permission === "can_edit_items") return true;
    return val ?? false;
  }, [user]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (e) { /* use defaults */ }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) { console.error("Failed to fetch clients:", e); }
  }, []);

  const fetchClientGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client-groups`);
      setClientGroups(res.data);
    } catch (e) { console.error("Failed to fetch client groups:", e); }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/line-items`);
      setItems(res.data);
    } catch (e) {
      console.error("Failed to fetch items:", e);
    }
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setDashboardStats(res.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, []);

  // Debounced stats refresh used by all mutations — avoids hammering the stats
  // endpoint on rapid user actions (500ms window)
  const debouncedFetchStats = useCallback(() => {
    clearTimeout(statsDebounceRef.current);
    statsDebounceRef.current = setTimeout(fetchDashboardStats, 500);
  }, [fetchDashboardStats]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchItems(), fetchDashboardStats()]);
      const now = Date.now();
      setLastRefreshTime(now);
      lastRefreshTimeRef.current = now;
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchItems, fetchDashboardStats]);

  // ── Smart background auto-refresh ─────────────────────────────────────────
  // Polls every 45 s while the tab is visible.
  // On tab focus-regain, refreshes immediately if data is more than 30 s stale.
  // Pauses automatically when the tab is hidden — zero wasted requests.
  useEffect(() => {
    const POLL_MS   = 45_000; // polling cadence
    const STALE_MS  = 30_000; // treat data as stale after this long
    let timer = null;

    const schedulePoll = () => {
      timer = setTimeout(async () => {
        if (document.visibilityState === "visible") {
          // Refresh silently; update ref inside refreshData
          await refreshData();
        }
        schedulePoll(); // re-arm regardless — catches the next visible tick
      }, POLL_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Wait 5 s before deciding to refresh — avoids a wasted round-trip when
        // the user simply alt-tabs or glances at another window momentarily.
        setTimeout(() => {
          if (document.visibilityState !== "visible") return; // left again, skip
          const staleBy = Date.now() - lastRefreshTimeRef.current;
          if (staleBy > STALE_MS) refreshData();
        }, 5_000);
      }
    };

    schedulePoll();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial data load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([refreshData(), fetchSettings(), fetchClients(), fetchClientGroups()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Granular item patcher ──
  const patchItem = useCallback((id, patch) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  }, []);

  // ── Mutation 1: advanceItem ──
  const advanceItem = useCallback(async (itemId, opts = {}) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    const idx = snapshot ? STAGES.indexOf(snapshot.stage) : -1;
    const nextStage = idx >= 0 && idx < STAGES.length - 1 ? STAGES[idx + 1] : null;

    // Optimistic patch
    if (snapshot && nextStage) patchItem(itemId, { stage: nextStage, new_to_stage: true });

    try {
      // partialOk: an assembly moving to Manufacturing with child parts still
      // outstanding — the user confirmed it in the partial-advance dialog.
      const res = await axios.post(`${API}/line-items/${itemId}/advance`, opts.partialOk ? { partial_ok: true } : undefined);
      patchItem(itemId, res.data);       // reconcile with server
      debouncedFetchStats();
      if (nextStage) {
        setLastAdvanceInfo({ toStage: nextStage, time: Date.now() });
        setTimeout(() => setLastAdvanceInfo(null), 2500);
      }
      toast.success(`Advanced to ${nextStage || "next stage"}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const undo = await axios.post(`${API}/line-items/${itemId}/return`, { return_reason: "Undo advance", is_rework: false });
              patchItem(itemId, undo.data);
              debouncedFetchStats();
            } catch {
              if (snapshot) patchItem(itemId, snapshot);
            }
          },
        },
      });
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error(e.response?.data?.detail || "Failed to advance item");
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 2: returnItem ──
  const returnItem = useCallback(async (itemId, returnReason = null, isRework = false, drawingLinks = null) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    const idx = snapshot ? STAGES.indexOf(snapshot.stage) : -1;
    const prevStage = idx > 0 ? STAGES[idx - 1] : null;

    // Optimistic patch
    if (snapshot && prevStage) patchItem(itemId, { stage: prevStage, new_to_stage: true });

    try {
      const payload = { return_reason: returnReason, is_rework: isRework };
      if (Array.isArray(drawingLinks)) payload.drawing_links = drawingLinks;
      const res = await axios.post(`${API}/line-items/${itemId}/return`, payload);
      patchItem(itemId, res.data);       // reconcile with server
      debouncedFetchStats();
      toast.success(`Returned to ${prevStage || "previous stage"}`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const undo = await axios.post(`${API}/line-items/${itemId}/advance`);
              patchItem(itemId, undo.data);
              debouncedFetchStats();
            } catch {
              if (snapshot) patchItem(itemId, snapshot);
            }
          },
        },
      });
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error(e.response?.data?.detail || "Failed to return item");
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 2b: returnItemToCustomer ──
  // A FAILED QC part is sent back to the customer (not reworked) — moves to
  // Dispatch flagged as a QC-failed return, carrying a modified return value.
  const returnItemToCustomer = useCallback(async (itemId, returnValueUsd = 0, returnNote = "") => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    if (snapshot) patchItem(itemId, {
      stage: "Dispatch", new_to_stage: true, qc_failed_return: true,
      return_value_usd: Number(returnValueUsd) || 0, return_note: returnNote,
    });
    try {
      const res = await axios.post(`${API}/line-items/${itemId}/return-to-customer`,
        { return_value_usd: Number(returnValueUsd) || 0, return_note: returnNote });
      patchItem(itemId, res.data);
      debouncedFetchStats();
      toast.success("Returned to customer — sent to Dispatch");
      return true;
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error(e.response?.data?.detail || "Failed to return to customer");
      return false;
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 3: blockItem ──
  // Accepts either a legacy string (free-text reason) or an object
  // { block_reason, block_reason_code, block_reason_subteam } from the new BlockModal.
  const blockItem = useCallback(async (itemId, reasonOrPayload) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    const payload = typeof reasonOrPayload === "string"
      ? { block_reason: reasonOrPayload, block_reason_code: "", block_reason_subteam: "" }
      : {
          block_reason: reasonOrPayload?.block_reason || "",
          block_reason_code: reasonOrPayload?.block_reason_code || "",
          block_reason_subteam: reasonOrPayload?.block_reason_subteam || "",
          waiting_on_user_ids: reasonOrPayload?.waiting_on_user_ids || [],
          demand_next_action: reasonOrPayload?.demand_next_action !== false,
        };
    patchItem(itemId, {
      is_blocked: true,
      block_reason: payload.block_reason,
      block_reason_code: payload.block_reason_code,
      block_reason_subteam: payload.block_reason_subteam,
    });

    try {
      const res = await axios.post(`${API}/line-items/${itemId}/block`, payload);
      patchItem(itemId, res.data);       // reconcile with server
      debouncedFetchStats();
      toast.success("Problem reported");
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error(e.response?.data?.detail || "Failed to report problem");
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 4: unblockItem ──
  const unblockItem = useCallback(async (itemId) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    patchItem(itemId, { is_blocked: false, block_reason: "" });

    try {
      const res = await axios.post(`${API}/line-items/${itemId}/unblock`);
      patchItem(itemId, res.data);       // reconcile with server
      debouncedFetchStats();
      toast.success("Item unblocked");
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error("Failed to unblock item");
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 4b: blocks v2 — add one owner-first block, clear one block ──
  // Several blocks may be open at once; the server keeps the legacy flat
  // block_* fields as a mirror of the oldest, so every existing reader is fine.
  const addBlock = useCallback(async (itemId, payload) => {
    try {
      const res = await axios.post(`${API}/line-items/${itemId}/blocks`, payload);
      patchItem(itemId, res.data);
      debouncedFetchStats();
      toast.success(`Blocked — waiting on ${payload?.party || payload?.owner || "someone"}`);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add block");
      throw e;
    }
  }, [patchItem, debouncedFetchStats]);

  const clearBlock = useCallback(async (itemId, blockId, clearNote = "") => {
    try {
      const res = await axios.post(`${API}/line-items/${itemId}/blocks/${blockId}/clear`, { clear_note: clearNote });
      patchItem(itemId, res.data);
      debouncedFetchStats();
      toast.success(res.data?.is_blocked ? "Block cleared — others still open" : "Item unblocked");
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear block");
      throw e;
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 5: updateStatus ──
  const updateStatus = useCallback(async (itemId, field, value, eta = null) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    const optimistic = { [field]: value };
    if (field === "material_status" || field === "tooling_status" || field === "gauging_status") {
      const etaField = { material_status: "material_eta", tooling_status: "tooling_eta", gauging_status: "gauging_eta" }[field];
      optimistic[etaField] = value === "Ordered" ? (eta || "") : "";
    }
    patchItem(itemId, optimistic);

    try {
      const payload = { field, value };
      if (eta !== null) payload.eta = eta;
      const res = await axios.patch(`${API}/line-items/${itemId}/status`, payload);
      patchItem(itemId, res.data);       // reconcile with server
      debouncedFetchStats();
      toast.success("Status updated");
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation: setRouterCard ── (Manufacturing gate Step 1)
  const setRouterCard = useCallback(async (itemId, routerCardNumber) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    const rc = (routerCardNumber || "").trim() || null;
    patchItem(itemId, {
      router_card_number: rc,
      router_released_at: rc ? new Date().toISOString() : null,
    });
    try {
      const res = await axios.patch(`${API}/line-items/${itemId}/router`, { router_card_number: rc });
      patchItem(itemId, res.data);
      toast.success(rc ? "Router released" : "Router cleared");
      return res.data;
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);
      toast.error(e?.response?.data?.detail || "Failed to update router card");
      throw e;
    }
  }, [patchItem]);

  // ── Mutation: setKittingComplete ── (Manufacturing gate Step 2)
  // Phase 1b — Customer Design Approval gate (drilling's PO Received gate).
  // Mirrors setKittingComplete: optimistic patch, audit fields from response.
  const setDesignApproved = useCallback(async (itemId, approved) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    patchItem(itemId, {
      design_approved: approved,
      design_approved_at: approved ? new Date().toISOString() : null,
    });
    try {
      const res = await axios.patch(`${API}/line-items/${itemId}/design-approval`, { approved });
      patchItem(itemId, res.data);
      toast.success(approved ? "Design approved" : "Design approval cleared");
      return res.data;
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);
      toast.error(e?.response?.data?.detail || "Failed to update design approval");
      throw e;
    }
  }, [patchItem]);

  const setKittingComplete = useCallback(async (itemId, kittingComplete) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    patchItem(itemId, {
      kitting_complete: kittingComplete,
      kitting_complete_at: kittingComplete ? new Date().toISOString() : null,
    });
    try {
      const res = await axios.patch(`${API}/line-items/${itemId}/kitting`, { kitting_complete: kittingComplete });
      patchItem(itemId, res.data);
      toast.success(kittingComplete ? "Kitting confirmed" : "Kitting un-confirmed");
      return res.data;
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);
      toast.error(e?.response?.data?.detail || "Failed to update kitting");
      throw e;
    }
  }, [patchItem]);

  // ── Mutation: setErpCode ── (per-PART accounting code; the backend
  // backfills every line sharing the part number across all stages/POs,
  // so we patch the clicked row optimistically and then do a full refresh
  // to pick up the backfilled siblings — same pattern as split lines.)
  const setErpCode = useCallback(async (itemId, erpCode) => {
    const code = (erpCode || "").trim() || null;
    try {
      const res = await axios.patch(`${API}/line-items/${itemId}/erp-code`, { erp_code: code });
      if (res.data?.item) patchItem(itemId, res.data.item);
      const n = res.data?.updated_count ?? 0;
      toast.success(code
        ? `ERP code saved — applied to ${n} line${n === 1 ? "" : "s"}`
        : "ERP code cleared");
      refreshData();
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save ERP code");
      throw e;
    }
  }, [patchItem, refreshData]);

  // ── Mutation: addUserNote ── (per-item notes)
  const addUserNote = useCallback(async (itemId, { text, eta } = {}) => {
    try {
      const res = await axios.post(`${API}/line-items/${itemId}/notes`, {
        text,
        eta: eta || null,
      });
      patchItem(itemId, res.data);
      toast.success("Note added");
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
      throw e;
    }
  }, [patchItem]);

  // ── Mutation 6: editItem ──
  const editItem = useCallback(async (itemId, updateData) => {
    const snapshot = itemsRef.current.find(i => i.id === itemId);
    patchItem(itemId, updateData);

    try {
      const res = await axios.put(`${API}/line-items/${itemId}`, updateData);
      patchItem(itemId, res.data);       // reconcile with server
      debouncedFetchStats();
      toast.success("Item updated");
      return res.data;
    } catch (e) {
      if (snapshot) patchItem(itemId, snapshot);   // rollback
      toast.error("Failed to update item");
      throw e;
    }
  }, [patchItem, debouncedFetchStats]);

  // ── Mutation 7: deleteItem ──
  const deleteItem = useCallback(async (itemId) => {
    const snapshotIdx = itemsRef.current.findIndex(i => i.id === itemId);
    const snapshot = itemsRef.current[snapshotIdx];
    // Optimistic removal
    setItems(prev => prev.filter(i => i.id !== itemId));

    try {
      await axios.delete(`${API}/line-items/${itemId}`);
      debouncedFetchStats();
      toast.success("Item deleted", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              // Re-create from snapshot (server assigns a new id)
              const { id: _id, created_at: _ca, updated_at: _ua, stage_ages: _sa, new_to_stage: _nts, ...createData } = snapshot;
              const res = await axios.post(`${API}/line-items`, createData);
              setItems(prev => {
                const list = [...prev];
                list.splice(Math.min(snapshotIdx, list.length), 0, res.data);
                return list;
              });
              debouncedFetchStats();
            } catch {
              toast.error("Could not undo deletion");
            }
          },
        },
      });
    } catch (e) {
      // Rollback: restore item at original position
      setItems(prev => {
        const list = [...prev];
        list.splice(Math.min(snapshotIdx, list.length), 0, snapshot);
        return list;
      });
      toast.error(e.response?.data?.detail || "Failed to delete item");
    }
  }, [debouncedFetchStats]);

  // ── Non-mutation ops (bulk/create — full refetch still appropriate) ──
  const createItem = useCallback(async (itemData) => {
    try {
      const res = await axios.post(`${API}/line-items`, itemData);
      setItems(prev => [...prev, res.data]);
      debouncedFetchStats();
      toast.success("Item created");
    } catch (e) {
      toast.error("Failed to create item");
    }
  }, [debouncedFetchStats]);

  const bulkCreateItems = useCallback(async (itemsData, silent = false) => {
    try {
      const res = await axios.post(`${API}/line-items/bulk`, itemsData);
      if (!silent) {
        toast.success(`Imported ${res.data.count} items`);
        await refreshData();
      }
      return res.data;
    } catch (e) {
      toast.error("Failed to import items");
      throw e;
    }
  }, [refreshData]);

  const bulkUpsertItems = useCallback(async (itemsData, silent = false) => {
    try {
      const res = await axios.post(`${API}/line-items/bulk-upsert`, itemsData);
      if (!silent) {
        toast.success(res.data.message);
        await refreshData();
      }
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upsert items");
      throw e;
    }
  }, [refreshData]);

  const bulkUpdateItemsById = useCallback(async (entries, silent = false) => {
    try {
      const res = await axios.post(`${API}/line-items/bulk-update-by-id`, entries);
      if (!silent) {
        toast.success(res.data.message);
        await refreshData();
      }
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update items");
      throw e;
    }
  }, [refreshData]);

  // ── Memoised context values ──
  const dataStateValue = useMemo(() => ({
    items, dashboardStats, loading, isRefreshing, lastRefreshTime,
  }), [items, dashboardStats, loading, isRefreshing, lastRefreshTime]);

  const dataActionsValue = useMemo(() => ({
    patchItem, refreshData,
    advanceItem, returnItem, returnItemToCustomer, blockItem, unblockItem, addBlock, clearBlock, updateStatus,
    setRouterCard, setKittingComplete, setDesignApproved, setErpCode, addUserNote,
    createItem, editItem, deleteItem, bulkCreateItems, bulkUpsertItems, bulkUpdateItemsById,
  }), [patchItem, refreshData, advanceItem, returnItem, returnItemToCustomer, blockItem, unblockItem, addBlock, clearBlock,
      updateStatus, setRouterCard, setKittingComplete, setDesignApproved, setErpCode, addUserNote,
      createItem, editItem, deleteItem, bulkCreateItems, bulkUpsertItems, bulkUpdateItemsById]);

  // Backward-compat merged value so existing DataContext consumers keep working
  const dataLegacyValue = useMemo(() => ({
    ...dataStateValue, ...dataActionsValue,
  }), [dataStateValue, dataActionsValue]);

  const themeValue        = useMemo(() => ({ theme, themePref, setThemePref, cycleTheme, flatMode, toggleFlat, easyRead, toggleEasyRead }), [theme, themePref, setThemePref, cycleTheme, flatMode, toggleFlat, easyRead, toggleEasyRead]);
  const roleValue         = useMemo(() => ({ canEditStage, canViewStage, canDo }), [canEditStage, canViewStage, canDo]);
  const filterValue       = useMemo(() => ({ globalFilter, setGlobalFilter }), [globalFilter, setGlobalFilter]);
  const divisionLensValue = useMemo(() => ({ lens: divisionLens, setLens: setDivisionLensPersist, lockedDivision: lensLockedDivision }), [divisionLens, setDivisionLensPersist, lensLockedDivision]);
  const divisionConfigValue = useMemo(() => ({ config: divisionConfig, refreshDivisionConfig }), [divisionConfig, refreshDivisionConfig]);
  const advanceValue      = useMemo(() => ({ lastAdvanceInfo }), [lastAdvanceInfo]);
  const settingsValue     = useMemo(() => ({ settings, setSettings, fetchSettings }), [settings, setSettings, fetchSettings]);
  const clientGroupsValue = useMemo(() => ({ clientGroups, setClientGroups, fetchClientGroups }), [clientGroups, setClientGroups, fetchClientGroups]);
  const clientsValue      = useMemo(() => ({ clients, setClients, fetchClients }), [clients, setClients, fetchClients]);

  return (
    <ThemeContext.Provider value={themeValue}>
      <RoleContext.Provider value={roleValue}>
        <DataStateContext.Provider value={dataStateValue}>
          <DataActionsContext.Provider value={dataActionsValue}>
            <DataContext.Provider value={dataLegacyValue}>
              <FilterContext.Provider value={filterValue}>
              <DivisionLensContext.Provider value={divisionLensValue}>
              <DivisionConfigContext.Provider value={divisionConfigValue}>
              <AdvanceContext.Provider value={advanceValue}>
              <SettingsContext.Provider value={settingsValue}>
              <ClientGroupsContext.Provider value={clientGroupsValue}>
              <ClientsContext.Provider value={clientsValue}>
              <DueFilterProvider>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                  <Route path="/po-received" element={<ErrorBoundary><StagePage stage="PO Received" /></ErrorBoundary>} />
                  <Route path="/procurement" element={<ErrorBoundary><StagePage stage="Procurement" /></ErrorBoundary>} />
                  <Route path="/manufacturing" element={<ErrorBoundary><StagePage stage="Manufacturing" /></ErrorBoundary>} />
                  <Route path="/qc-check" element={<ErrorBoundary><StagePage stage="Quality Control" /></ErrorBoundary>} />
                  <Route path="/dispatch" element={<ErrorBoundary><StagePage stage="Dispatch" /></ErrorBoundary>} />
                  <Route path="/completed" element={<ErrorBoundary><StagePage stage="Completed" /></ErrorBoundary>} />
                  <Route path="/stores" element={<ErrorBoundary><StoresPage /></ErrorBoundary>} />
                  <Route path="/scrap-sales" element={<ErrorBoundary><ScrapSalesPage /></ErrorBoundary>} />
                  <Route path="/analyst" element={<ErrorBoundary><AnalystPage /></ErrorBoundary>} />
                  {/* Blocked Board — the management review surface (plan §7).
                      Permission-gated inside the page; the route itself is open
                      so a direct link explains itself instead of bouncing. */}
                  <Route path="/blocked-board" element={<ErrorBoundary><BlockedBoardPage /></ErrorBoundary>} />
                  <Route path="/changelog" element={<ErrorBoundary><Changelog /></ErrorBoundary>} />
                </Route>
                {/* Stock Center — dual-access users (Admin etc.) switch in here.
                    Its own StockLayout shell, OUTSIDE the ERP <Layout>. Gated:
                    users without Stock Center access are sent back to the ERP. */}
                <Route
                  path="/stock-center"
                  element={hasStockCenterAccess(user) ? <StockLayout /> : <Navigate to="/" replace />}
                >
                  <Route index element={<ErrorBoundary><StockCenterPage /></ErrorBoundary>} />
                </Route>
                <Route
                  path="/ecotile"
                  element={hasEcotileAccess(user) ? <EcotileLayout /> : <Navigate to="/" replace />}
                >
                  <Route index element={<ErrorBoundary><EcotilePage /></ErrorBoundary>} />
                </Route>
                {/* Maintenance — dual-access users (production, admins) switch
                    in from the Modules dropdown. Its own shell, OUTSIDE the ERP
                    <Layout>; users without the permission go back to the ERP. */}
                <Route
                  element={hasMaintenanceAccess(user) ? <MaintenanceLayout /> : <Navigate to="/" replace />}
                >
                  <Route path="/maintenance" element={<ErrorBoundary><MaintenanceDashboard /></ErrorBoundary>} />
                  {MAINTENANCE_STAGE_ROUTES}
                  {MAINTENANCE_EXTRA_ROUTES}
                </Route>
              </Routes>
              </DueFilterProvider>
              </ClientsContext.Provider>
              </ClientGroupsContext.Provider>
              </SettingsContext.Provider>
              </AdvanceContext.Provider>
              </DivisionConfigContext.Provider>
              </DivisionLensContext.Provider>
              </FilterContext.Provider>
            </DataContext.Provider>
          </DataActionsContext.Provider>
        </DataStateContext.Provider>
      </RoleContext.Provider>
    </ThemeContext.Provider>
  );
}

// ── Root app with auth ──
function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // 401 interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      r => r,
      err => {
        if (err.response?.status === 401 && !err.config?.url?.includes('/auth/')) {
          localStorage.removeItem('rl_token');
          localStorage.removeItem('rl_user');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Check existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('rl_token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.get(`${API}/auth/me`)
      .then(res => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('rl_token');
        localStorage.removeItem('rl_user');
        delete axios.defaults.headers.common['Authorization'];
      })
      .finally(() => setAuthLoading(false));
  }, []);

  const _applySession = useCallback((token, userData) => {
    localStorage.setItem('rl_token', token);
    localStorage.setItem('rl_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    _applySession(res.data.token, res.data.user);
  }, [_applySession]);

  const loginWithOtp = useCallback(async (email, code) => {
    const res = await axios.post(`${API}/auth/otp/verify`, { email, code });
    _applySession(res.data.token, res.data.user);
  }, [_applySession]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { /* ignore */ }
    localStorage.removeItem('rl_token');
    localStorage.removeItem('rl_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-600/30 border-t-orange-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, loginWithOtp, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/*" element={<ProtectedContent />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
